import { ExpandMore } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { Sstv2Migration } from "shared/sstv2Migration"
import { Sstv2MigrationThingTypeMrn } from "shared/sstv2MigrationData"
import { Tenant } from "shared/tenant"
import { TenantThingTypeMrn } from "shared/tenantData"
import { useAppDispatch, useAppSelector } from "src/app/hooks"
import { selectMeIsReady } from "src/features/me/meSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectSaList, thingsActions } from "src/features/things/thingsSlice"
import Sstv2MigrationMetrics from "./Sstv2MigrationMetrics"
import Sstv2MigrationGrid from "./Sstv2MigrationGrid"
import Sstv2MigrationDrawer from "./Sstv2MigrationDrawer"
import Sstv2MigrationControls from "./Sstv2MigrationControls"
import Sstv2ReconciliationPanel from "./Sstv2ReconciliationPanel"
import Sstv2FleetRollup from "./Sstv2FleetRollup"
import Sstv2MigrationFlowDoc from "./Sstv2MigrationFlowDoc"
import Sstv2IngestionDlqPanel from "./Sstv2IngestionDlqPanel"
import { useSstv2MigrationView } from "./useSstv2MigrationView"
import { Sstv2MigrationRow } from "./sstv2MigrationTypes"

// Polling cadence for the live view (Item 2). Scoped to THIS panel only.
// Paused automatically when the tab is unfocused (`skipPollingIfUnfocused`)
// and when the toggle is off.
const POLL_INTERVAL_MS = 20000
// Wall-clock tick driving "updated Xs ago" + stall/elapsed columns.
const NOW_TICK_MS = 5000

/**
 * Collapsed-by-default section. Everything below the job table is either
 * fleet-level context or reference material — useful, but not what the page is
 * for. Keeping it folded is what makes the lifecycle table the page.
 */
const SecondarySection: FC<{
  title: string
  children: React.ReactNode
}> = ({ title, children }) => (
  <Accordion disableGutters sx={{ mb: 2 }}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
)

/**
 * The migration lifecycle view: ONE ROW PER MIGRATION JOB, keyed by the
 * `migrationId` (the partner's `sl2_locker_id` for that attempt). Owns its own
 * queries — they only run while this tab is mounted.
 *
 * Layout intent: live job state first (numbers → table → drawer), and every
 * derived/reference surface folded below it.
 */
export const Sstv2MigrationLifecyclePanel: FC = () => {
  const { t } = useTranslation()
  const isReady = useAppSelector(selectMeIsReady)
  const dispatch = useAppDispatch()

  const [autoRefresh, setAutoRefresh] = useState(true)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const [selectedRow, setSelectedRow] = useState<Sstv2MigrationRow | null>(null)

  useEffect(() => {
    dispatch(thingsActions.clearSaList())
  }, [dispatch])

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), NOW_TICK_MS)
    return () => clearInterval(id)
  }, [])

  const pollingInterval = autoRefresh ? POLL_INTERVAL_MS : 0
  const subscription = {
    skip: !isReady,
    pollingInterval,
    skipPollingIfUnfocused: true,
  }

  // LOCKER-353: ask for the slim projection. The full `sstLocker`/`sst2Locker`
  // snapshots are ~93% of this response and pushed it past Lambda's 6 MB
  // ceiling, so every 20s poll of this panel returned a 5xx in prod.
  //
  // What this panel needs OUT of those snapshots is `is_online` (the per-locker
  // online dot) and `sstLocker._id` (the locker-NAME fallback inside
  // `getLockerDisplayValues`, which this tab reaches from four places — the
  // accordion, `useSstv2MigrationView`, `Sstv2FleetRollup`'s search and
  // `Sstv2ReconciliationPanel`). The slim shape keeps exactly those; the
  // authoritative list is `KEPT_SNAPSHOT_FIELDS` in `Ap_SaGetLockers.ts`.
  //
  // Do not trim that list from here. An earlier revision of this comment said
  // the panel read "exactly one field", which was wrong and would have shipped a
  // regression: the name fallback is an indirect read through a helper in
  // another directory, so a grep for `sstLocker` in this folder does not find
  // it. Check the READERS, not the attribute name.
  const q = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetLockers,
        "ap.limit": 2001,
        "ap.noSstLocker": true,
      },
    },
    subscription,
  )
  const qTenants = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
        "ap.tenantOnly": true,
        "ap.limit": 1000,
      },
    },
    subscription,
  )
  const qMigrations = useGetThingsQuery(
    { params: { "ap.name": SharedAccessPatternSaEnum.SaGetSstv2Migrations, "ap.limit": 2001 } },
    subscription,
  )

  const tenants = useAppSelector((state) => selectSaList<Tenant>(state)).filter(
    (t) => t.thingTypeMrn === TenantThingTypeMrn,
  )
  const lockers = useAppSelector((state) => selectSaList<Locker>(state)).filter(
    (t) => t.thingTypeMrn === LockerThingTypeMrn,
  )
  const migrations = useAppSelector((state) =>
    selectSaList<Sstv2Migration>(state),
  ).filter((t) => t.thingTypeMrn === Sstv2MigrationThingTypeMrn)

  const isLoading =
    q.isLoading || q.isFetching || qTenants.isLoading || qTenants.isFetching
  const isMigrationsLoading =
    qMigrations.isLoading || qMigrations.isFetching || isLoading
  const isFetching = q.isFetching || qTenants.isFetching || qMigrations.isFetching

  const errorLabels = useMemo(() => {
    const labels: string[] = []
    if (qMigrations.isError) labels.push(t("migrationEntities"))
    if (q.isError) labels.push(t("lockers"))
    if (qTenants.isError) labels.push(t("tenants"))
    return labels
  }, [q.isError, qTenants.isError, qMigrations.isError, t])

  const onRetry = useCallback(() => {
    q.refetch()
    qTenants.refetch()
    qMigrations.refetch()
  }, [q, qTenants, qMigrations])

  const { migrationData, totals, migrationRows, migrationCounts } =
    useSstv2MigrationView(lockers, tenants, migrations, nowMs)

  return (
    <div data-ui="UI-SL2W-251" style={{ display: "contents" }}>
      <Sstv2MigrationControls
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={setAutoRefresh}
        lastUpdatedMs={qMigrations.fulfilledTimeStamp}
        nowMs={nowMs}
        isFetching={isFetching}
        onRefresh={onRetry}
        errorLabels={errorLabels}
        onRetry={onRetry}
      />

      {/* ── Live job state: the reason this page exists ── */}
      <Sstv2MigrationMetrics counts={migrationCounts} loading={isMigrationsLoading} />

      {/* Above the table, and not collapsible — but it renders NOTHING
          while the ingestion DLQ is empty, which is the normal case. A
          dead-lettered migration ingest means mapping items that were never
          written, so the flip cannot miss it, stalled-flip detection cannot see it
          and no counter moves. Putting that behind a fold would be the same as not
          having it: the whole point is that it appears unbidden. */}
      <Sstv2IngestionDlqPanel />

      <Box sx={{ mb: 4 }}>
        <Sstv2MigrationGrid
          rows={migrationRows}
          loading={isMigrationsLoading}
          onRowClick={setSelectedRow}
          nowMs={nowMs}
        />
      </Box>
      <Sstv2MigrationDrawer row={selectedRow} onClose={() => setSelectedRow(null)} />

      {/* ── Everything below is context, not live job state ── */}
      <SecondarySection title={t("migrationReconciliation")}>
        <Sstv2ReconciliationPanel
          lockers={lockers}
          rows={migrationRows}
          loading={isMigrationsLoading}
        />
      </SecondarySection>

      <SecondarySection title={t("migrationFleetRollup")}>
        <Sstv2FleetRollup
          migrationData={migrationData}
          totals={totals}
          loading={isLoading}
        />
      </SecondarySection>

      <Sstv2MigrationFlowDoc />
    </div>
  )
}

export default Sstv2MigrationLifecyclePanel
